import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";

import { Observable, Subscription, NEVER, combineLatest } from "rxjs";
import { filter, take, map, switchMap } from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";

import { ConfigurationService } from "../services/configuration.service";
import { WebCallTopicService } from "../../../../common/services/api/resources/webCallTopic/web-call-topic.service";
import { CONFIG_SCHEMA_VERSION } from "../../../../common/interfaces/organization-configuration";
import { UserCustomizationService } from "../../../../common/services/api/resources/userCustomization/user-customization.service";
import { UserService } from "../../../../common/services/api/resources/user/user.service";
import { OrganizationService } from "../../../../common/services/api/resources/organization/organization.service";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";

import { views } from "../../../app/phone/views";
import { isAdminRole } from "@onsip/common/services/api/role";

import { SaysoPricingModalComponent } from "./saysoPricing/saysoPricingModal/sayso-pricing-modal.component";
import { OutdatedConfigVersionComponent } from "./errorModals/outdatedVersion/outdated-version.component";

@Component({
  templateUrl: "./sayso-settings.component.html",
  styleUrls: ["./sayso-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SaysoSettingsComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;

  allTopicsDisabled!: Observable<boolean>;
  hasTopics!: Observable<boolean>;
  showPreview!: Observable<boolean>;
  views = views; // for template

  // SaysoPricing Toggle Variables
  /** flag hooked to SaysoBilling flag from OrganizationRead */
  saysoPricingEnabled = false;
  saysoPricingAnalyticsToggle = false;
  saysoPricingScreenshareToggle = false;
  saysoPricingBrandingToggle = false;

  private unsubscriber = new Subscription();

  constructor(
    private config: ConfigurationService,
    private breakpoint: BreakpointObserver,
    private dialog: MatDialog,
    private router: Router,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private webCallTopicService: WebCallTopicService,
    private cdRef: ChangeDetectorRef,
    private userService: UserService,
    private userCustomization: UserCustomizationService,
    private orgService: OrganizationService
  ) {}

  ngOnInit() {
    this.unsubscriber.add(
      this.config.state
        .pipe(
          filter(state => !!state.orgConfig.schemaVersion),
          take(1),
          switchMap(orgInfo => {
            const version = orgInfo.orgConfig.schemaVersion;

            if (CONFIG_SCHEMA_VERSION < version) {
              if (this.dialog.openDialogs.length === 0) this.openUpdateDialog();

              return this.router.events.pipe(filter(e => e instanceof NavigationEnd));
            }
            return NEVER;
          })
        )
        .subscribe(() => {
          if (this.router.url === views.SAYSO_SETTINGS && this.dialog.openDialogs.length === 0) {
            this.openUpdateDialog();
          } else if (this.router.url !== views.SAYSO_SETTINGS) {
            this.dialog.closeAll();
          }
        })
    );

    this.unsubscriber.add(
      combineLatest({
        user: this.userService.selfUser,
        org: this.orgService.organization,
        userCustom: this.userCustomization.selfUser,
        configState: this.config.state.pipe(take(1))
      })
        .pipe(take(1))
        .subscribe(({ user, org, userCustom, configState }) => {
          if (isAdminRole(user.roles)) {
            this.saysoPricingEnabled = org.saysoBilling;
            this.saysoPricingAnalyticsToggle = userCustom.userInfo?.sayso?.showAnalytics || false;
            this.saysoPricingBrandingToggle =
              configState.orgConfig.branding === "removed" ? true : false;
            this.saysoPricingScreenshareToggle = configState.orgConfig.allowScreenshare || false;
            // Turn off analytics if saysoBilling is disabled and analytics toggle is on
            if (!this.saysoPricingEnabled && this.saysoPricingAnalyticsToggle) {
              this.saysoPricingAnalyticsToggle = false;
              this.editUserCustomizationAnalytics(false);
            }
            this.cdRef.markForCheck();
          }
        })
    );

    this.showPreview = this.breakpoint
      .observe([Breakpoints.XLarge, Breakpoints.Large])
      .pipe(map(state => state.matches));
    this.allTopicsDisabled = this.config.state.pipe(map(state => !!state.orgConfig.disabled));
    this.hasTopics = this.webCallTopicService.orgState.pipe(map(state => state.length > 0));
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  successSnackbar() {
    return this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.CHANGES_SAVED"),
      "success"
    );
  }

  setAllDisabled(disabled: boolean): void {
    this.config.editConfigProperty("disabled", disabled).then(() => {
      this.snackbar.openSnackBar(
        disabled
          ? this.translate.instant("ONSIP_I18N.SAYSO_HAS_BEEN_DISABLED")
          : this.translate.instant("ONSIP_I18N.SAYSO_HAS_BEEN_ENABLED"),
        "success"
      );
    });
  }

  openSaysoPricingDialog(): void {
    const dialog = this.dialog.open(SaysoPricingModalComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"]
    });

    this.unsubscriber.add(
      dialog
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          // we should not be setting the toggle to true here if the user is a free user
          // this is handled by the api flag which is turned on after the user pays via sales call
          this.saysoPricingAnalyticsToggle = false;
          this.saysoPricingBrandingToggle = false;
          this.saysoPricingScreenshareToggle = false;
          this.cdRef.markForCheck();
        })
    );
  }

  enableSaysoPricingAnalytics(event: MatSlideToggleChange): void {
    if (event.checked && !this.saysoPricingEnabled) this.openSaysoPricingDialog();
    if (this.saysoPricingEnabled) {
      this.editUserCustomizationAnalytics(event.checked);
      this.snackbar.openSnackBar(
        event.checked
          ? this.translate.instant("ONSIP_I18N.SAYSO_ANALYTICS_HAS_BEEN_ENABLED")
          : this.translate.instant("ONSIP_I18N.SAYSO_ANALYTICS_HAS_BEEN_DISABLED"),
        "success"
      );
    }
  }

  enableSaysoBranding(event: MatSlideToggleChange): void {
    if (event.checked && !this.saysoPricingEnabled) this.openSaysoPricingDialog();
    if (this.saysoPricingEnabled) {
      const brand = event.checked ? "removed" : "onsip";
      this.config.editConfigProperty("branding", brand).then(() => {
        this.snackbar.openSnackBar(
          event.checked
            ? this.translate.instant("ONSIP_I18N.ONSIP_BRANDING_HAS_BEEN_REMOVED")
            : this.translate.instant("ONSIP_I18N.ONSIP_BRANDING_HAS_BEEN_ADDED"),
          "success"
        );
      });
    }
  }

  enableSaysoScreenShare(event: MatSlideToggleChange): void {
    if (event.checked && !this.saysoPricingEnabled) this.openSaysoPricingDialog();
    if (this.saysoPricingEnabled) {
      this.config.editConfigProperty("allowScreenshare", event.checked).then(() => {
        this.snackbar.openSnackBar(
          event.checked
            ? this.translate.instant("ONSIP_I18N.SAYSO_SCREENSHARE_HAS_BEEN_ENABLED")
            : this.translate.instant("ONSIP_I18N.SAYSO_SCREENSHARE_HAS_BEEN_DISABLED"),
          "success"
        );
      });
    }
  }

  private editUserCustomizationAnalytics(showAnalytics: boolean): void {
    this.userCustomization.userCustomizationSaysoEdit({
      "Sayso[ShowAnalytics]": showAnalytics
    });
  }

  private openUpdateDialog(): void {
    this.dialog.open(OutdatedConfigVersionComponent, {
      disableClose: true,
      closeOnNavigation: false,
      hasBackdrop: true,
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      backdropClass: "onsip-dialog-outdated-sayso-settings"
    });
  }
}
